import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, PlayCircle } from 'lucide-react';
import { Button } from '../ui/button';
import TutorialModal from './TutorialModal';
import { TUTORIAL_STORAGE } from '../../config/tutorial';

/**
 * Reusable launcher for the RendPleX tutorial.
 *
 * Variants:
 *   - "button"  : compact button (used in help menu / secondary spots)
 *   - "card"    : full dashboard card (matches the "▶ Watch Tutorial" spec)
 *   - "inline"  : text-only link (for embedding in prose)
 */
export default function TutorialLauncher({ variant = 'button', className = '', label, onCtaAnalyze }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const hasCompleted = (() => {
    try {
      return localStorage.getItem(TUTORIAL_STORAGE.COMPLETED) === 'true';
    } catch { return false; }
  })();

  const trigger = hasCompleted ? 'replay' : 'launcher';

  if (variant === 'card') {
    return (
      <>
        <button
          data-testid="tutorial-launcher-card"
          onClick={() => setOpen(true)}
          className={`group text-left w-full bg-gradient-to-br from-[#12355B] to-[#1565C0] hover:from-[#1565C0] hover:to-[#12355B] rounded-2xl p-5 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-[1px] ${className}`}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#45C04A] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
              <PlayCircle className="w-6 h-6 text-[#12355B]" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-widest text-[#45C04A] font-bold">
                {t('tutorial.card.eyebrow')}
              </p>
              <p className="mt-0.5 font-primary font-bold text-white text-lg leading-tight">
                {t('tutorial.card.title')}
              </p>
              <p className="mt-1 text-sm text-white/75 line-clamp-2">
                {t('tutorial.card.description')}
              </p>
            </div>
          </div>
        </button>
        <TutorialModal open={open} onClose={() => setOpen(false)} trigger={trigger} onCtaAnalyze={onCtaAnalyze} />
      </>
    );
  }

  if (variant === 'inline') {
    return (
      <>
        <button
          data-testid="tutorial-launcher-inline"
          onClick={() => setOpen(true)}
          className={`inline-flex items-center gap-1.5 text-sm font-semibold text-[#12355B] dark:text-[#45C04A] hover:underline ${className}`}
        >
          <Play className="w-3.5 h-3.5" />
          {label || t('tutorial.watchTutorial')}
        </button>
        <TutorialModal open={open} onClose={() => setOpen(false)} trigger={trigger} onCtaAnalyze={onCtaAnalyze} />
      </>
    );
  }

  if (variant === 'hero') {
    return (
      <>
        <Button
          data-testid="hero-tutorial-button"
          onClick={() => setOpen(true)}
          className={`bg-white hover:bg-slate-50 dark:bg-white/10 dark:hover:bg-white/20 text-[#12355B] dark:text-white border-2 border-[#12355B] dark:border-white/20 rounded-xl font-semibold transition-all duration-300 hover:-translate-y-[1px] shadow-button group ${className}`}
        >
          <Play className="w-5 h-5 mr-2 fill-current text-[#45C04A] transition-transform group-hover:scale-110" />
          {label || t('tutorial.watchTutorial')}
        </Button>
        <TutorialModal open={open} onClose={() => setOpen(false)} trigger={trigger} onCtaAnalyze={onCtaAnalyze} />
      </>
    );
  }

  return (
    <>
      <Button
        data-testid="tutorial-launcher-button"
        onClick={() => setOpen(true)}
        variant="outline"
        className={`rounded-xl border-slate-200 dark:border-slate-700 text-[#12355B] dark:text-white hover:border-[#45C04A] ${className}`}
      >
        <Play className="w-4 h-4 mr-2" />
        {label || t('tutorial.watchTutorial')}
      </Button>
      <TutorialModal open={open} onClose={() => setOpen(false)} trigger={trigger} onCtaAnalyze={onCtaAnalyze} />
    </>
  );
}